/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { HDMPriceDisplay } from '@/components/blocks/HDMPriceDisplay';
import { LinkWrap, Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { PromotionCard } from '@/components/blocks/PromotionCard';
import { RatingCard } from '@/components/blocks/RatingCard';
import { Swatch } from '@/components/blocks/Swatch';
import { FeaturedProductSkeleton } from '@/components/content/FeaturedProduct/parts/FeaturedProductSkeleton';
import { useProduct } from '@/data/Content/Product';
import { useProductCard } from '@/data/Content/_ProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { useLocalization } from '@/data/Localization';
import { ProductType, RatingType } from '@/data/types/Product';
import { replaceImagePath } from '@/utils/hdmPathConverter';
import { Box, Grid, Paper, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useTrackSelectItem } from 'presentation/events/hooks/useTrackSelectItem';
import { FC, useMemo } from 'react';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

export const FeaturedProduct: FC<{
	id: string;
	clickAction?: () => void;
	sx?: SxProps<Theme>;
	// eslint-disable-next-line complexity
}> = ({ id: partNumber, clickAction, sx }) => {
	const { product, loading } = useProduct({ id: partNumber });
	const ratings: RatingType | undefined = product?.ratings;
	const { onSwatch, sku } = useProductCard({ partNumber } as ProductType);
	const priceDisplayNLS = useLocalization('PriceDisplay');
	const featuredCardNLS = useLocalization('FeaturedCard');
	const { onClick } = useProductEvents({ product: product as ProductType });
	const fullImg = replaceImagePath((sku ?? product)?.fullImage);
	const trackSelectItem = useTrackSelectItem();
	const { navigation } = useGetCategories();
	const categories = useMemo(() => {
		if (!product?.parentCatalogGroupID) return [];
		return findParentCategories(navigation, product.parentCatalogGroupID);
	}, [navigation, product?.parentCatalogGroupID]);
	const handleLinkImageOnClick = () => {
		onClick(clickAction);
		if (product) {
			trackSelectItem({
				product,
				categories,
			});
		}
	};

	if (loading) {
		return <FeaturedProductSkeleton sx={sx} />;
	}

	if (!product) {
		return null;
	}

	return (
		<Paper id={`featureCard_${product?.id}`} data-testid={`featureCard_${product?.id}`} sx={sx}>
			<Grid container alignItems="center" spacing={3} px={2} py={4}>
				<Grid
					item
					xs={12}
					sm={6}
					lg={5}
					id={`featureCard_imageContainer_${product?.id}`}
					data-testid={`featureCard_imageContainer_${product?.id}`}
				>
					<LinkWrap
						href={product?.seo?.href || ''}
						onClick={handleLinkImageOnClick}
						data-testid={`featureCard_imageRouter_${product?.id}`}
						id={`featureCard_imageRouter_${product?.id}`}
						passHref={false}
						legacyBehavior={false}
					>
						<Img
							width="100%"
							id={`featureCard_fullImage_${product?.id}`}
							data-testid={`featureCard_fullImage_${product?.id}`}
							src={fullImg}
							alt={product?.name}
						/>
					</LinkWrap>
				</Grid>
				<Grid
					item
					xs={12}
					sm={6}
					md={5}
					lg={6}
					id={`featureCard_grid_${product?.id}`}
					data-testid={`featureCard_grid_${product?.id}`}
				>
					<Stack spacing={2}>
						<Box component="header">
							{product?.manufacturer ? (
								<Typography variant="overline">{product?.manufacturer}</Typography>
							) : null}
							<Typography
								variant="h4"
								id={`featureCard_describer_${product?.id}`}
								data-testid={`featureCard_describer_${product?.id}`}
							>
								{product?.name}
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							{product?.colorSwatches
								? product.colorSwatches.map((colorSwatch) => (
										<Swatch
											title={colorSwatch.identifier}
											key={colorSwatch.identifier}
											image={colorSwatch.image1path}
											id={`feature-product-${colorSwatch.identifier.toLowerCase()}-swatch`}
											data-testid={`feature-product-${colorSwatch.identifier.toLowerCase()}-swatch`}
											onClick={(event) => onSwatch(event, colorSwatch)}
										/>
								  ))
								: null}
						</Stack>

						<RatingCard ratings={ratings} />
						<Box>
							<Typography
								variant="h4"
								id={`featureCard_price_${product?.id}`}
								data-testid={`featureCard_price_${product?.id}`}
							>
								{product?.productPrice?.offer ? (
									<HDMPriceDisplay price={product?.productPrice} />
								) : (
									priceDisplayNLS.Labels.Pending.t()
								)}
							</Typography>
						</Box>
						<Box>
							<Linkable
								type="button"
								href={product?.seo?.href || ''}
								variant="contained"
								data-testid={`featureCard_textRouter_${product?.id}_shop_now`}
								id={`featureCard_textRouter_${product?.id}_shop_now`}
								color="secondary"
								onClick={onClick(clickAction)}
							>
								{featuredCardNLS.ShopNow.t()}
							</Linkable>
						</Box>
						<Box>SKU: {product?.partNumber} | Modelo:</Box>
						<PromotionCard
							id={product?.id}
							attributes={product?.attributes}
							showDescription={true}
							partNumber={product?.partNumber}
						/>
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	);
};
